import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, LayoutGrid, Sparkles, Users, Zap, Star, Shield } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function Landing() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-[#FAFAFA] relative">
            {/* Nav */}
            <header className="glass-nav sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center">
                    <Logo />
                    <nav className="hidden md:flex items-center gap-8 mx-auto text-sm text-slate-600">
                        <a href="#features" className="hover:text-slate-900 transition-colors" data-testid="nav-features">Features</a>
                        <a href="#how" className="hover:text-slate-900 transition-colors" data-testid="nav-how">How it works</a>
                        <a href="#pricing" className="hover:text-slate-900 transition-colors" data-testid="nav-pricing">Pricing</a>
                    </nav>
                    <div className="ml-auto flex items-center gap-2">
                        <Link to="/login" className="text-sm font-medium text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="cta-login">Sign in</Link>
                        <Link to="/signup" className="btn-terra text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2" data-testid="cta-signup-nav">
                            Start free <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 dot-grid opacity-40" />
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-20 relative">
                    <div className="grid lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-8 fade-in">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-200 bg-white text-xs text-slate-600 mb-6">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#C55A46]" />
                                Now for design & consulting agencies
                            </div>
                            <h1 className="font-display text-5xl md:text-6xl lg:text-[76px] leading-[0.98] tracking-tighter text-slate-900">
                                Your client portal that <span className="italic text-[#C55A46]">updates&nbsp;itself</span>.
                            </h1>
                            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
                                ClientSpace turns your existing tools — Notion, Drive, ClickUp, Stripe — into a clean, professional portal your clients actually love. No workflow changes. No new CRM.
                            </p>
                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <button onClick={() => navigate("/signup")} data-testid="hero-cta-signup" className="btn-terra px-6 py-3.5 rounded-lg text-sm font-medium inline-flex items-center gap-2">
                                    Start free — 5 min setup <ArrowRight className="w-4 h-4" />
                                </button>
                                <a href="#how" className="px-6 py-3.5 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-700 hover:border-slate-300 transition-colors" data-testid="hero-cta-how">
                                    See how it works
                                </a>
                            </div>
                            <div className="mt-8 flex items-center gap-6 text-xs text-slate-500">
                                <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#10B981]" /> No credit card required</div>
                                <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#10B981]" /> Client-facing white-label</div>
                                <div className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#10B981]" /> Cancel anytime</div>
                            </div>
                        </div>

                        {/* Right decorative card */}
                        <div className="lg:col-span-4 fade-in">
                            <div className="relative">
                                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="overline">Client</p>
                                            <p className="font-display text-xl mt-1">Northwind Coffee</p>
                                        </div>
                                        <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Needs review</span>
                                    </div>
                                    <div className="mt-6">
                                        <div className="flex justify-between text-xs text-slate-500 mb-2">
                                            <span>Website Redesign</span><span>65%</span>
                                        </div>
                                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                            <div className="h-full bg-[#C55A46]" style={{ width: "65%" }} />
                                        </div>
                                    </div>
                                    <div className="mt-5 space-y-2.5">
                                        {[
                                            { label: "Homepage design v2", tone: "review" },
                                            { label: "Component library", tone: "in_progress" },
                                            { label: "Brand exploration", tone: "delivered" },
                                        ].map((d) => (
                                            <div key={d.label} className="flex items-center justify-between text-sm">
                                                <span className="text-slate-700">{d.label}</span>
                                                <span className={`text-xs px-2 py-0.5 rounded-full border ${
                                                    d.tone === "delivered" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                                    d.tone === "review" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                                    "bg-slate-50 text-slate-600 border-slate-200"
                                                }`}>{d.tone.replace("_", " ")}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="pointer-events-none absolute -bottom-4 -left-4 w-24 h-24 rounded-2xl bg-[#C55A46]/10 -z-10 blur-xl" />
                            </div>
                        </div>
                    </div>

                    {/* Trust bar */}
                    <div className="mt-20 pt-8 border-t border-slate-200">
                        <p className="overline text-center mb-6">Loved by boutique agencies & consultancies</p>
                        <div className="flex flex-wrap justify-center gap-x-12 gap-y-4 text-slate-400 font-display text-lg">
                            <span>Halcyon</span><span>Meridian</span><span>Vellum &amp; Ink</span><span>Orbit</span><span>Field &amp; Fern</span><span>Lumen</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features - Bento */}
            <section id="features" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                <div className="max-w-2xl mb-14">
                    <p className="overline mb-3">The intelligent client layer</p>
                    <h2 className="font-display text-4xl md:text-5xl tracking-tight text-slate-900">A portal that reflects the work you're already doing.</h2>
                </div>
                <div className="grid md:grid-cols-6 gap-4">
                    <FeatureCard className="md:col-span-4" icon={LayoutGrid} title="A single home for every project"
                        body="Overview, progress, deliverables, approvals and files — always up to date, in one polished view." />
                    <FeatureCard className="md:col-span-2" icon={Users} title="Made for clients, not project managers"
                        body="Zero learning curve. Your clients see exactly what they need — nothing more." />
                    <FeatureCard className="md:col-span-2" icon={Zap} title="Connect your stack"
                        body="Notion, Drive, ClickUp, Stripe — flowing in soon. Your workflow stays untouched." />
                    <FeatureCard className="md:col-span-4" icon={Sparkles} title="AI updates written for you"
                        body="Weekly status notes drafted automatically from your project activity. You approve, we send." />
                    <FeatureCard className="md:col-span-3" icon={Shield} title="White-label ready"
                        body="Your brand, your domain, your voice. ClientSpace disappears into the background." />
                    <FeatureCard className="md:col-span-3" icon={Star} title="Client-first, agency-approved"
                        body="Approvals, comments and file requests — without the enterprise weight." />
                </div>
            </section>

            {/* How it works */}
            <section id="how" className="border-t border-slate-200 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                    <div className="grid md:grid-cols-12 gap-10">
                        <div className="md:col-span-4">
                            <p className="overline mb-3">How it works</p>
                            <h2 className="font-display text-4xl tracking-tight text-slate-900">Three steps. That's the whole thing.</h2>
                            <p className="mt-4 text-slate-600 leading-relaxed">No onboarding call needed. Most agencies are live before their next client meeting.</p>
                        </div>
                        <div className="md:col-span-8 grid gap-4">
                            {[
                                { n: "01", t: "Add your client", b: "Company, contact, project. Portal is ready in seconds." },
                                { n: "02", t: "Connect your tools (soon)", b: "Bring in progress from Notion, files from Drive, tasks from ClickUp, invoices from Stripe." },
                                { n: "03", t: "Share the link", b: "Your client opens a beautiful, live portal — and everyone stays aligned." },
                            ].map((s) => (
                                <div key={s.n} className="rounded-xl border border-slate-200 p-6 flex gap-6 hover-lift bg-white">
                                    <span className="font-mono text-sm text-slate-400 pt-1">{s.n}</span>
                                    <div>
                                        <h3 className="font-display text-xl text-slate-900">{s.t}</h3>
                                        <p className="text-slate-600 mt-1.5">{s.b}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing teaser */}
            <section id="pricing" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                <div className="rounded-2xl bg-[#0F172A] p-10 md:p-16 relative overflow-hidden">
                    <div className="grid md:grid-cols-2 gap-10 items-center relative z-10">
                        <div>
                            <p className="overline text-slate-400 mb-3">Pricing</p>
                            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-white">Start free. Grow when you're ready.</h2>
                            <p className="mt-4 text-slate-300 max-w-md">Every plan includes unlimited client portals, white-labeling, and premium templates.</p>
                            <div className="mt-6 flex gap-3">
                                <button onClick={() => navigate("/signup")} data-testid="pricing-cta-signup" className="btn-terra px-6 py-3 rounded-lg text-sm font-medium">Start free</button>
                                <a href="#features" className="px-6 py-3 rounded-lg text-sm font-medium border border-white/20 text-white hover:bg-white/10 transition-colors">See features</a>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { name: "Starter", price: "$0", note: "3 clients" },
                                { name: "Studio", price: "$29", note: "unlimited clients" },
                            ].map((p) => (
                                <div key={p.name} className="rounded-xl bg-white/5 border border-white/10 p-6">
                                    <p className="overline text-slate-400">{p.name}</p>
                                    <p className="font-display text-3xl text-white mt-2">{p.price}<span className="text-sm text-slate-400 font-normal">/mo</span></p>
                                    <p className="text-slate-400 text-sm mt-1">{p.note}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <footer className="border-t border-slate-200 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-wrap items-center gap-6 justify-between">
                    <Logo size="sm" />
                    <p className="text-sm text-slate-500">© 2026 ClientSpace. Crafted for the people behind the work.</p>
                </div>
            </footer>
        </div>
    );
}

function FeatureCard({ icon: Icon, title, body, className = "" }) {
    return (
        <div className={`rounded-xl border border-slate-200 bg-white p-7 hover-lift ${className}`}>
            <div className="w-9 h-9 rounded-lg bg-[#FEF2F2] flex items-center justify-center mb-5">
                <Icon className="w-4.5 h-4.5 text-[#C55A46]" />
            </div>
            <h3 className="font-display text-xl text-slate-900 tracking-tight">{title}</h3>
            <p className="text-slate-600 mt-2 leading-relaxed">{body}</p>
        </div>
    );
}
